import { Card } from '@app/components/various/Card';
import { Status } from '@app/components/various/Status';
import { getOrderPathByStatus } from '@app/logic/Orders';
import { useOrderStatusLabel } from '@app/utils/hooks';
import { useOrderListOptions } from '@app/utils/hooks/order/useOrderListOptions';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getOrderStatusColor } from '@app/utils/status';
import { Row } from '@tanstack/react-table';
import { DeliveryWindow, Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Props {
  row: Row<Order.Essentials>;
  deliveryWindows: DeliveryWindow.Mixed[];
  onCopyToNewSelection: (order: Order.Essentials) => void;
  onLinesheetDownload: (order: Order.Essentials) => void;
  onDelete: (order: Order.Essentials) => void;
}

export const SelectionsListCard = ({ row, deliveryWindows, onCopyToNewSelection, onDelete, onLinesheetDownload }: Props) => {
  const {
    accountName,
    buyerFirstName,
    buyerLastName,
    deliveryWindows: deliveryWindowIds,
    name,
    order: orderId,
    orderNumber,
    poNumber,
    createdDate,
    status,
    totals,
  } = row.original;

  const { t } = useTranslation(['buyers', 'common', 'selections', 'checkout']);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'selectionsTable' });
  const statusLabel = useOrderStatusLabel(status);

  const getDeliveryWindowByIds = React.useCallback(
    (deliveryWindowsIds: DeliveryWindow.Id[]) => {
      return deliveryWindowsIds
        .map(delwinId => deliveryWindows.find(({ deliveryWindow }) => delwinId === deliveryWindow))
        .filter(isDefined);
    },
    [deliveryWindows],
  );

  const accountTitle = columnsVisibility.account?.isVisible ? ` - ${accountName}` : '';
  const title = `#${orderNumber} ${accountTitle}`;
  const menuItems = useOrderListOptions({
    onCopyToNewSelection,
    onDelete,
    onLinesheetDownload,
    order: row.original,
  });

  return (
    <Card.Wrapper markerColor={getOrderStatusColor(status)}>
      {columnsVisibility.status?.isVisible && (
        <Card.Header>
          <Status type={status} />
          <span>
            {t('common:status')}: <span className="text-neutral-20">{statusLabel}</span>
          </span>
        </Card.Header>
      )}
      <Card.Title>
        <Link data-sentry-mask to={getOrderPathByStatus(orderId, status)} className="absolute-cursor-pointer focus-visible:focus-outline">
          {title}
        </Link>
      </Card.Title>
      <Card.AttributeList className="flex-1">
        <Card.Attribute value={name} name={t('selections:selection_name')} />
        {columnsVisibility.delivery?.isVisible && (
          <Card.Attribute
            value={getDeliveryWindowByIds(deliveryWindowIds)
              .map(delwin => delwin.name)
              .join(', ')}
            name={t('common:delivery_window_other')}
          />
        )}
        {columnsVisibility.poNumber?.isVisible && <Card.Attribute value={poNumber} name={t('checkout:po_number')} />}
        {columnsVisibility.createdDate?.isVisible && <Card.Attribute value={createdDate} name={t('common:created')} />}
      </Card.AttributeList>
      <Card.SummaryList>
        {columnsVisibility.buyer?.isVisible && (
          <Card.Summary
            name={t('buyers:buyer_one')}
            value={[buyerFirstName, buyerLastName].filter(value => isDefined(value) && !isEmpty(value)).join(' ') || '-'}
          />
        )}
        {columnsVisibility.total?.isVisible && (
          <Card.Summary name={t('common:total_for_units', { count: totals?.itemsCount })} value={totals?.grandTotalWithoutTax} />
        )}
      </Card.SummaryList>
      <Card.Menu title={title} items={menuItems} className="z-interactiveTableItem" />
    </Card.Wrapper>
  );
};
